import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import useStyles from './styles';
import { Box, Button, Checkbox, Chip, IconButton, List, ListItem, Paper, TextField, Typography } from '@mui/material';
import { Icon, InputWithIcon, ExpandedTooltip } from '@livable/saas-react-components';
import { useStore } from 'src/stores';
import { SimpleConfirmationModal } from 'src/components/shared/SimpleConfirmationModal';
import { AddPricingModal } from './AddPricingModal';
import { AddProductModal } from './AddProductModal';
import { getTieredPriceRowLabel, sortTierRowsOpenEndedLast } from './tierLabels';
function formatPriceAmount(val) {
  return typeof val === 'number' ? "$".concat(Number(val).toFixed(2)) : String(val !== null && val !== void 0 ? val : '');
}

/** Min price row for table when API sends a positive minimum (tiered and non-tiered). */
function buildMinPriceBlock(p) {
  var _p$minimumPrice, _p$tierTableResponse;
  const raw = (_p$minimumPrice = p.minimumPrice) !== null && _p$minimumPrice !== void 0 ? _p$minimumPrice : (_p$tierTableResponse = p.tierTableResponse) === null || _p$tierTableResponse === void 0 ? void 0 : _p$tierTableResponse.minimumPrice;
  if (raw == null || raw === '') return [];
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n) || n <= 0) return [];
  return [{
    type: 'Min price',
    amount: formatPriceAmount(n)
  }];
}
export const PpricingsTab = observer(() => {
  var _store$stripe$priceDa, _store$stripe$deleteP, _store$stripe$deleteP2, _store$stripe$bulkDel;
  const classes = useStyles();
  const store = useStore();
  const records = (_store$stripe$priceDa = store.stripe.priceDashboard) !== null && _store$stripe$priceDa !== void 0 ? _store$stripe$priceDa : [];
  const _useState = useState(() => new Set()),
    _useState2 = _slicedToArray(_useState, 2),
    collapsedIds = _useState2[0],
    setCollapsedIds = _useState2[1];
  const _useState3 = useState(() => new Set()),
    _useState4 = _slicedToArray(_useState3, 2),
    selectedPricingIds = _useState4[0],
    setSelectedPricingIds = _useState4[1];
  const _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    addPricingModalOpen = _useState6[0],
    setAddPricingModalOpen = _useState6[1];
  const _useState7 = useState('add'),
    _useState8 = _slicedToArray(_useState7, 2),
    addPricingModalMode = _useState8[0],
    setAddPricingModalMode = _useState8[1];
  const _useState9 = useState(null),
    _useState0 = _slicedToArray(_useState9, 2),
    pricingToEdit = _useState0[0],
    setPricingToEdit = _useState0[1];
  const _useState1 = useState(''),
    _useState10 = _slicedToArray(_useState1, 2),
    defaultProductId = _useState10[0],
    setDefaultProductId = _useState10[1];
  const _useState11 = useState(false),
    _useState12 = _slicedToArray(_useState11, 2),
    addProductModalOpen = _useState12[0],
    setAddProductModalOpen = _useState12[1];
  const _useState13 = useState('add'),
    _useState14 = _slicedToArray(_useState13, 2),
    addProductModalMode = _useState14[0],
    setAddProductModalMode = _useState14[1];
  const _useState15 = useState(null),
    _useState16 = _slicedToArray(_useState15, 2),
    productToEdit = _useState16[0],
    setProductToEdit = _useState16[1];
  const _useState17 = useState(false),
    _useState18 = _slicedToArray(_useState17, 2),
    deleteConfirmOpen = _useState18[0],
    setDeleteConfirmOpen = _useState18[1];
  const _useState19 = useState(null),
    _useState20 = _slicedToArray(_useState19, 2),
    pricingToDelete = _useState20[0],
    setPricingToDelete = _useState20[1];
  const _useState21 = useState(false),
    _useState22 = _slicedToArray(_useState21, 2),
    deleteProductConfirmOpen = _useState22[0],
    setDeleteProductConfirmOpen = _useState22[1];
  const _useState23 = useState(null),
    _useState24 = _slicedToArray(_useState23, 2),
    productToDelete = _useState24[0],
    setProductToDelete = _useState24[1];
  const _useState25 = useState(false),
    _useState26 = _slicedToArray(_useState25, 2),
    bulkDeleteConfirmOpen = _useState26[0],
    setBulkDeleteConfirmOpen = _useState26[1];
  const _useState27 = useState(''),
    _useState28 = _slicedToArray(_useState27, 2),
    searchQuery = _useState28[0],
    setSearchQuery = _useState28[1];
  const q = (searchQuery || '').trim().toLowerCase();
  const filteredRecords = !q ? records.map(r => _objectSpread(_objectSpread({}, r), {}, {
    originalIndices: (r.priceResponse || []).map((_, i) => i)
  })) : records.map(record => {
    const productMatches = (record.productName || '').toLowerCase().includes(q);
    const priceResponse = record.priceResponse || [];
    let matchingPricings, originalIndices;
    if (productMatches) {
      matchingPricings = priceResponse;
      originalIndices = priceResponse.map((_, i) => i);
    } else {
      const withIdx = priceResponse.map((p, i) => ({
        p,
        i
      })).filter(_ref => {
        let p = _ref.p;
        return (p.name || '').toLowerCase().includes(q) || (p.type || '').toLowerCase().includes(q);
      });
      matchingPricings = withIdx.map(_ref2 => {
        let p = _ref2.p;
        return p;
      });
      originalIndices = withIdx.map(_ref3 => {
        let i = _ref3.i;
        return i;
      });
    }
    if (matchingPricings.length === 0) return null;
    return _objectSpread(_objectSpread({}, record), {}, {
      priceResponse: matchingPricings,
      originalIndices
    });
  }).filter(Boolean);
  const allPricingIdsFromFiltered = filteredRecords.flatMap(r => (r.originalIndices || []).map(oi => "".concat(r.productId, "-").concat(oi)));
  const toggleCollapsed = id => {
    setCollapsedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else next.add(id);
      return next;
    });
  };
  const allSelected = allPricingIdsFromFiltered.length > 0 && allPricingIdsFromFiltered.every(id => selectedPricingIds.has(id));
  const someSelected = allPricingIdsFromFiltered.some(id => selectedPricingIds.has(id));
  const handleSelectAll = (_, checked) => {
    setSelectedPricingIds(checked ? new Set(allPricingIdsFromFiltered) : new Set());
  };
  const handleTogglePricing = pricingId => {
    setSelectedPricingIds(prev => {
      const next = new Set(prev);
      if (next.has(pricingId)) next.delete(pricingId);else next.add(pricingId);
      return next;
    });
  };
  const handleClearPricingSelection = () => setSelectedPricingIds(new Set());
  const selectedPricingsForBulkDelete = (_store$stripe$priceDa2 => {
    const list = [];
    const dashboard = (_store$stripe$priceDa2 = store.stripe.priceDashboard) !== null && _store$stripe$priceDa2 !== void 0 ? _store$stripe$priceDa2 : [];
    dashboard.forEach(record => {
      (record.priceResponse || []).forEach((p, idx) => {
        const compositeId = "".concat(record.productId, "-").concat(idx);
        if (selectedPricingIds.has(compositeId) && (p.priceId || p.id)) {
          var _p$priceId, _p$name;
          list.push({
            priceId: (_p$priceId = p.priceId) !== null && _p$priceId !== void 0 ? _p$priceId : p.id,
            name: (_p$name = p.name) !== null && _p$name !== void 0 ? _p$name : compositeId
          });
        }
      });
    });
    return list;
  })();
  const handleDeletePricing = () => {
    setBulkDeleteConfirmOpen(true);
  };
  const closeBulkDeleteConfirm = () => setBulkDeleteConfirmOpen(false);
  const handleConfirmBulkDelete = async _ref4 => {
    let action = _ref4.action,
      objectToPassOver = _ref4.objectToPassOver;
    if (action === 'cancel') {
      closeBulkDeleteConfirm();
      return;
    }
    if (action === 'submit' && Array.isArray(objectToPassOver === null || objectToPassOver === void 0 ? void 0 : objectToPassOver.priceIds) && objectToPassOver.priceIds.length > 0) {
      try {
        await store.stripe.bulkDeletePricesAsync.run({
          priceIds: objectToPassOver.priceIds
        });
        await store.stripe.getPriceDashboardAsync.run();
        await store.stripe.getPricesAsync.run();
        handleClearPricingSelection();
      } catch (err) {
        console.error('Bulk delete prices failed:', err);
      }
      closeBulkDeleteConfirm();
    }
  };
  const hasPricingSelection = selectedPricingIds.size > 0;
  const allProductIds = filteredRecords.map(r => r.productId);
  const allExpanded = allProductIds.length === 0 || allProductIds.every(id => !collapsedIds.has(id));
  const toggleExpandCollapseAll = () => {
    if (allExpanded) {
      setCollapsedIds(new Set(allProductIds));
    } else {
      setCollapsedIds(new Set());
    }
  };
  const openAddPricingModal = function () {
    let recordId = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
    setAddPricingModalMode('add');
    setPricingToEdit(null);
    setDefaultProductId(recordId);
    setAddPricingModalOpen(true);
  };
  const openEditPricingModal = (pricing, record) => {
    var _rawPrice$meterEvent, _ref5, _record$productId, _rawPrice$name, _rawPrice$priceId;
    setAddPricingModalMode('edit');
    const rawPrice = ((record === null || record === void 0 ? void 0 : record.priceResponse) || []).find(p => (p.priceId || p.id) === (pricing.priceId || pricing.id));
    const meterEventRaw = (_rawPrice$meterEvent = rawPrice === null || rawPrice === void 0 ? void 0 : rawPrice.meterEvent) !== null && _rawPrice$meterEvent !== void 0 ? _rawPrice$meterEvent : pricing === null || pricing === void 0 ? void 0 : pricing.meterEvent;
    const meterEvent = meterEventRaw != null && String(meterEventRaw).trim() !== '' ? String(meterEventRaw).trim() : 'None';
    setPricingToEdit(_objectSpread(_objectSpread({}, rawPrice || pricing), {}, {
      productId: (_ref5 = (_record$productId = record === null || record === void 0 ? void 0 : record.productId) !== null && _record$productId !== void 0 ? _record$productId : rawPrice === null || rawPrice === void 0 ? void 0 : rawPrice.productId) !== null && _ref5 !== void 0 ? _ref5 : '',
      name: (_rawPrice$name = rawPrice === null || rawPrice === void 0 ? void 0 : rawPrice.name) !== null && _rawPrice$name !== void 0 ? _rawPrice$name : pricing.name,
      priceId: (_rawPrice$priceId = rawPrice === null || rawPrice === void 0 ? void 0 : rawPrice.priceId) !== null && _rawPrice$priceId !== void 0 ? _rawPrice$priceId : pricing.priceId,
      meterEvent
    }));
    setDefaultProductId('');
    setAddPricingModalOpen(true);
  };
  const closeAddPricingModal = () => {
    setAddPricingModalOpen(false);
    setPricingToEdit(null);
  };
  const handleSavePricing = () => {
    closeAddPricingModal();
  };
  const openAddProductModal = () => {
    setAddProductModalMode('add');
    setProductToEdit(null);
    setAddProductModalOpen(true);
  };
  const openEditProductModal = record => {
    setAddProductModalMode('edit');
    setProductToEdit({
      id: record.productId,
      name: record.productName || ''
    });
    setAddProductModalOpen(true);
  };
  const closeAddProductModal = () => {
    setAddProductModalOpen(false);
    setProductToEdit(null);
  };
  const handleSaveProduct = () => closeAddProductModal();
  const openDeleteProductConfirm = record => {
    setProductToDelete({
      id: record.productId,
      name: record.productName || ''
    });
    setDeleteProductConfirmOpen(true);
  };
  const closeDeleteProductConfirm = () => {
    setDeleteProductConfirmOpen(false);
    setProductToDelete(null);
  };
  const handleConfirmDeleteProduct = async _ref6 => {
    let action = _ref6.action,
      objectToPassOver = _ref6.objectToPassOver;
    if (action === 'cancel') {
      closeDeleteProductConfirm();
      return;
    }
    if (action === 'submit' && objectToPassOver !== null && objectToPassOver !== void 0 && objectToPassOver.id) {
      try {
        await store.stripe.deleteProductAsync.run({
          id: objectToPassOver.id
        });
        closeDeleteProductConfirm();
        await store.stripe.getPriceDashboardAsync.run();
        await store.stripe.getAllProductsAsync.run();
      } catch (err) {
        console.error('Delete product failed:', err);
      }
    }
  };
  const openDeleteConfirm = pricing => {
    setPricingToDelete({
      priceId: pricing.priceId,
      name: pricing.name
    });
    setDeleteConfirmOpen(true);
  };
  const closeDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
    setPricingToDelete(null);
  };
  const handleConfirmDelete = async _ref7 => {
    let action = _ref7.action,
      objectToPassOver = _ref7.objectToPassOver;
    if (action === 'cancel') {
      closeDeleteConfirm();
      return;
    }
    if (action === 'submit' && objectToPassOver !== null && objectToPassOver !== void 0 && objectToPassOver.priceId) {
      try {
        await store.stripe.deletePriceAsync.run({
          id: objectToPassOver.priceId
        });
        closeDeleteConfirm();
        await store.stripe.getPriceDashboardAsync.run();
        await store.stripe.getPricesAsync.run();
      } catch (err) {
        console.error('Delete price failed:', err);
      }
    }
  };
  return /*#__PURE__*/React.createElement(Box, {
    className: classes.subscriptionsRoot
  }, /*#__PURE__*/React.createElement(Box, {
    className: classes.subscriptionsToolbar
  }, /*#__PURE__*/React.createElement(Box, {
    className: classes.subscriptionsToolbarLeft
  }, /*#__PURE__*/React.createElement(InputWithIcon, {
    size: "small",
    icon: /*#__PURE__*/React.createElement(Icon, {
      type: "search"
    })
  }, /*#__PURE__*/React.createElement(TextField, {
    size: "small",
    variant: "outlined",
    placeholder: "Search",
    fullWidth: true,
    value: searchQuery,
    onChange: e => setSearchQuery(e.target.value)
  })), filteredRecords.length > 0 && /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    size: "small",
    onClick: toggleExpandCollapseAll
  }, allExpanded ? 'Collapse all' : 'Expand all')), /*#__PURE__*/React.createElement(Box, {
    className: classes.subscriptionsToolbarRight
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    size: "small",
    startIcon: /*#__PURE__*/React.createElement(Icon, {
      type: "plus"
    }),
    onClick: openAddProductModal
  }, "Add product"))), /*#__PURE__*/React.createElement(AddProductModal, {
    isOpen: addProductModalOpen,
    onClose: closeAddProductModal,
    onSave: handleSaveProduct,
    mode: addProductModalMode,
    product: productToEdit
  }), q && filteredRecords.length === 0 ? /*#__PURE__*/React.createElement(Box, {
    className: classes.pricingsEmpty
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.secondary"
  }, "No pricings match your search.")) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    className: classes.pricingsSelectAllBar
  }, /*#__PURE__*/React.createElement(Checkbox, {
    size: "small",
    checked: allSelected,
    indeterminate: someSelected && !allSelected,
    onChange: handleSelectAll,
    className: classes.pricingsSelectAllCheckbox
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    className: classes.pricingsSelectAllLabel
  }, "Select all pricings")), /*#__PURE__*/React.createElement(Box, {
    className: classes.pricingsScrollWrapper
  }, filteredRecords.map(record => {
    var _record$priceResponse, _record$priceResponse2, _record$priceResponse3, _record$priceResponse4, _record$priceResponse5, _record$priceResponse6;
    const indices = record.originalIndices || [];
    const pricings = (record.priceResponse || []).map((p, idx) => {
      var _p$tierTableResponse$, _p$tierTableResponse2, _p$type, _p$type2, _indices$idx, _p$priceId2, _p$name2, _p$type3, _ref8, _p$subscriptionCount, _p$subscriptionRespon, _ref9, _p$isDefault;
      const isTiered = (p.type || '').toLowerCase() === 'tiered';
      const minPriceBlock = buildMinPriceBlock(p);
      const tierRows = sortTierRowsOpenEndedLast((_p$tierTableResponse$ = (_p$tierTableResponse2 = p.tierTableResponse) === null || _p$tierTableResponse2 === void 0 ? void 0 : _p$tierTableResponse2.tierPrice) !== null && _p$tierTableResponse$ !== void 0 ? _p$tierTableResponse$ : []);
      const tierPriceBlocks = isTiered && tierRows.length > 0 ? tierRows.map((tier, tierIdx) => ({
        type: getTieredPriceRowLabel(tierRows, tierIdx),
        amount: formatPriceAmount(tier.amount)
      })) : [];
      const prices = isTiered ? minPriceBlock.length > 0 || tierPriceBlocks.length > 0 ? [...minPriceBlock, ...tierPriceBlocks] : [{
        type: (_p$type = p.type) !== null && _p$type !== void 0 ? _p$type : '',
        amount: formatPriceAmount(p.fixedPrice)
      }] : [...minPriceBlock, {
        type: (_p$type2 = p.type) !== null && _p$type2 !== void 0 ? _p$type2 : '',
        amount: formatPriceAmount(p.fixedPrice)
      }];
      return {
        id: "".concat(record.productId, "-").concat((_indices$idx = indices[idx]) !== null && _indices$idx !== void 0 ? _indices$idx : idx),
        priceId: (_p$priceId2 = p.priceId) !== null && _p$priceId2 !== void 0 ? _p$priceId2 : '',
        name: (_p$name2 = p.name) !== null && _p$name2 !== void 0 ? _p$name2 : '',
        type: (_p$type3 = p.type) !== null && _p$type3 !== void 0 ? _p$type3 : '',
        subscriptionsCount: (_ref8 = (_p$subscriptionCount = p.subscriptionCount) !== null && _p$subscriptionCount !== void 0 ? _p$subscriptionCount : (_p$subscriptionRespon = p.subscriptionResponse) === null || _p$subscriptionRespon === void 0 ? void 0 : _p$subscriptionRespon.length) !== null && _ref8 !== void 0 ? _ref8 : 0,
        isDefault: (_ref9 = (_p$isDefault = p.isDefault) !== null && _p$isDefault !== void 0 ? _p$isDefault : p.default) !== null && _ref9 !== void 0 ? _ref9 : false,
        prices
      };
    });
    return /*#__PURE__*/React.createElement("div", {
      key: record.productId,
      className: classes.pricingsRecord
    }, /*#__PURE__*/React.createElement("div", {
      className: classes.pricingsRecordHeader
    }, /*#__PURE__*/React.createElement("div", {
      className: classes.pricingsRecordHeaderLeft
    }, /*#__PURE__*/React.createElement(IconButton, {
      size: "small",
      className: classes.pricingsRecordHeaderExpand,
      onClick: () => toggleCollapsed(record.productId),
      "aria-label": collapsedIds.has(record.productId) ? 'Expand' : 'Collapse'
    }, /*#__PURE__*/React.createElement(Box, {
      component: "span",
      sx: {
        display: 'inline-flex',
        transform: collapsedIds.has(record.productId) ? 'none' : 'rotate(90deg)'
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "arrow-line-right",
      fontSize: "large"
    }))), /*#__PURE__*/React.createElement("span", {
      className: classes.pricingsRecordHeaderTitle
    }, record.productName), /*#__PURE__*/React.createElement("span", {
      className: classes.pricingsRecordHeaderDivider
    }), /*#__PURE__*/React.createElement("span", {
      className: classes.pricingsRecordHeaderCount
    }, pricings.length, " pricings"), /*#__PURE__*/React.createElement("span", {
      className: classes.pricingsRecordHeaderDivider
    }), /*#__PURE__*/React.createElement(ExpandedTooltip, {
      title: "Add pricing",
      placement: "top"
    }, /*#__PURE__*/React.createElement(IconButton, {
      size: "small",
      className: classes.pricingsRecordHeaderAdd,
      "aria-label": "Add pricing",
      onClick: () => openAddPricingModal(record.productId)
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "plus"
    })))), /*#__PURE__*/React.createElement("div", {
      className: classes.pricingsRecordHeaderRight
    }, /*#__PURE__*/React.createElement(ExpandedTooltip, {
      title: "Edit product",
      placement: "top"
    }, /*#__PURE__*/React.createElement(IconButton, {
      size: "small",
      className: classes.pricingsRecordHeaderAdd,
      "aria-label": "Edit product",
      onClick: () => openEditProductModal(record)
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "edit"
    }))), /*#__PURE__*/React.createElement(ExpandedTooltip, {
      title: ((_record$priceResponse = (_record$priceResponse2 = record.priceResponse) === null || _record$priceResponse2 === void 0 ? void 0 : _record$priceResponse2.length) !== null && _record$priceResponse !== void 0 ? _record$priceResponse : 0) >= 1 ? 'Product with pricings cannot be deleted' : 'Delete product',
      placement: "top"
    }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(IconButton, {
      size: "small",
      className: "".concat(classes.pricingsRecordHeaderAdd, " ").concat(((_record$priceResponse3 = (_record$priceResponse4 = record.priceResponse) === null || _record$priceResponse4 === void 0 ? void 0 : _record$priceResponse4.length) !== null && _record$priceResponse3 !== void 0 ? _record$priceResponse3 : 0) >= 1 ? classes.pricingsDeleteButtonDefault : undefined),
      "aria-label": "Delete product",
      onClick: () => openDeleteProductConfirm(record),
      disabled: ((_record$priceResponse5 = (_record$priceResponse6 = record.priceResponse) === null || _record$priceResponse6 === void 0 ? void 0 : _record$priceResponse6.length) !== null && _record$priceResponse5 !== void 0 ? _record$priceResponse5 : 0) >= 1
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "trash"
    })))))), !collapsedIds.has(record.productId) && /*#__PURE__*/React.createElement("div", {
      className: classes.pricingsRecordContent
    }, pricings.map(pricing => {
      var _pricing$subscription, _pricing$subscription2, _pricing$subscription3, _pricing$subscription4;
      return /*#__PURE__*/React.createElement("div", {
        key: pricing.id,
        className: classes.pricingsContentRow
      }, /*#__PURE__*/React.createElement(Checkbox, {
        size: "small",
        className: classes.pricingsContentRowCheckbox,
        checked: selectedPricingIds.has(pricing.id),
        onChange: () => handleTogglePricing(pricing.id)
      }), /*#__PURE__*/React.createElement(Typography, {
        className: classes.pricingsContentRowName,
        variant: "body1"
      }, pricing.name), /*#__PURE__*/React.createElement("span", {
        className: classes.pricingsContentRowDivider
      }), /*#__PURE__*/React.createElement(Chip, {
        size: "small",
        label: pricing.type,
        variant: "info",
        className: classes.pricingsContentRowChip
      }), /*#__PURE__*/React.createElement("span", {
        className: classes.pricingsContentRowDivider
      }), /*#__PURE__*/React.createElement(Typography, {
        variant: "body2",
        color: "text.secondary",
        className: classes.pricingsContentRowSubs
      }, (_pricing$subscription = pricing.subscriptionsCount) !== null && _pricing$subscription !== void 0 ? _pricing$subscription : 0, ' ', pricing.subscriptionsCount === 1 ? 'Subscription' : 'Subscriptions'), /*#__PURE__*/React.createElement(Box, {
        className: classes.pricingsContentRowSpacer
      }), /*#__PURE__*/React.createElement(Box, {
        className: classes.pricingsContentRowPrices
      }, (pricing.prices || []).map((p, idx) => /*#__PURE__*/React.createElement("div", {
        key: idx,
        className: classes.pricingsPriceBlock
      }, /*#__PURE__*/React.createElement(Typography, {
        variant: "body2",
        color: "text.secondary",
        className: classes.pricingsPriceBlockTitle
      }, p.type), /*#__PURE__*/React.createElement(Typography, {
        variant: "body1",
        fontWeight: "bold",
        className: classes.pricingsPriceBlockAmount
      }, p.amount)))), /*#__PURE__*/React.createElement(Box, {
        className: classes.pricingsContentRowActions
      }, /*#__PURE__*/React.createElement(ExpandedTooltip, {
        title: "Edit pricing",
        placement: "top"
      }, /*#__PURE__*/React.createElement(IconButton, {
        "aria-label": "Edit",
        onClick: () => openEditPricingModal(pricing, record)
      }, /*#__PURE__*/React.createElement(Icon, {
        type: "edit"
      }))), /*#__PURE__*/React.createElement(ExpandedTooltip, {
        title: pricing.isDefault ? 'Default pricing cannot be deleted' : ((_pricing$subscription2 = pricing.subscriptionsCount) !== null && _pricing$subscription2 !== void 0 ? _pricing$subscription2 : 0) >= 1 ? 'Pricing with subscriptions cannot be deleted' : 'Delete pricing',
        placement: "top"
      }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(IconButton, {
        "aria-label": "Delete",
        onClick: () => openDeleteConfirm(pricing),
        disabled: !pricing.priceId || pricing.isDefault || ((_pricing$subscription3 = pricing.subscriptionsCount) !== null && _pricing$subscription3 !== void 0 ? _pricing$subscription3 : 0) >= 1,
        className: !pricing.priceId || pricing.isDefault || ((_pricing$subscription4 = pricing.subscriptionsCount) !== null && _pricing$subscription4 !== void 0 ? _pricing$subscription4 : 0) >= 1 ? classes.pricingsDeleteButtonDefault : undefined
      }, /*#__PURE__*/React.createElement(Icon, {
        type: "trash"
      }))))));
    })));
  }))), hasPricingSelection && /*#__PURE__*/React.createElement(Paper, {
    className: classes.selectionBar,
    elevation: 2
  }, /*#__PURE__*/React.createElement(Box, {
    className: classes.selectionBarLeft
  }, /*#__PURE__*/React.createElement(IconButton, {
    onClick: handleClearPricingSelection,
    "aria-label": "Clear selection"
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "close"
  })), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    component: "span"
  }, selectedPricingIds.size, " ", selectedPricingIds.size === 1 ? 'pricing' : 'pricings', " selected")), /*#__PURE__*/React.createElement(Box, {
    className: classes.selectionBarRight
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    size: "small",
    startIcon: /*#__PURE__*/React.createElement(Icon, {
      type: "trash"
    }),
    onClick: handleDeletePricing
  }, "Delete pricing"))), /*#__PURE__*/React.createElement(AddPricingModal, {
    isOpen: addPricingModalOpen,
    onClose: closeAddPricingModal,
    mode: addPricingModalMode,
    pricing: pricingToEdit,
    defaultProductId: defaultProductId,
    onSave: handleSavePricing
  }), /*#__PURE__*/React.createElement(SimpleConfirmationModal, {
    headerText: "Delete pricing",
    bodyText: pricingToDelete !== null && pricingToDelete !== void 0 && pricingToDelete.name ? "Do you really want to delete \"".concat(pricingToDelete.name, "\"?") : 'Do you really want to delete this pricing?',
    externalIsOpen: deleteConfirmOpen,
    callBack: handleConfirmDelete,
    objectToPassOver: pricingToDelete,
    customSubmitButtonName: "Delete",
    isLoading: (_store$stripe$deleteP = store.stripe.deletePriceAsync) === null || _store$stripe$deleteP === void 0 ? void 0 : _store$stripe$deleteP.inProgress
  }), /*#__PURE__*/React.createElement(SimpleConfirmationModal, {
    headerText: "Delete product",
    bodyText: productToDelete !== null && productToDelete !== void 0 && productToDelete.name ? "Do you really want to delete product \"".concat(productToDelete.name, "\"?") : 'Do you really want to delete this product?',
    externalIsOpen: deleteProductConfirmOpen,
    callBack: handleConfirmDeleteProduct,
    objectToPassOver: productToDelete,
    customSubmitButtonName: "Delete",
    isLoading: (_store$stripe$deleteP2 = store.stripe.deleteProductAsync) === null || _store$stripe$deleteP2 === void 0 ? void 0 : _store$stripe$deleteP2.inProgress
  }), /*#__PURE__*/React.createElement(SimpleConfirmationModal, {
    headerText: "Delete pricings",
    bodyComponent: /*#__PURE__*/React.createElement(Box, {
      className: classes.bulkDeleteConfirmBody
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "h4",
      className: classes.bulkDeleteConfirmTitle
    }, "Do you really want to delete the following pricings?"), /*#__PURE__*/React.createElement(List, {
      dense: true,
      disablePadding: true
    }, selectedPricingsForBulkDelete.map((p, i) => /*#__PURE__*/React.createElement(ListItem, {
      key: p.priceId || i,
      disablePadding: true
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body2"
    }, p.name || p.priceId))))),
    externalIsOpen: bulkDeleteConfirmOpen,
    callBack: handleConfirmBulkDelete,
    objectToPassOver: {
      priceIds: selectedPricingsForBulkDelete.map(p => p.priceId).filter(Boolean)
    },
    customSubmitButtonName: "Delete",
    isLoading: (_store$stripe$bulkDel = store.stripe.bulkDeletePricesAsync) === null || _store$stripe$bulkDel === void 0 ? void 0 : _store$stripe$bulkDel.inProgress
  }));
});